import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import {
  Users,
  Search,
  Building,
  Award,
  Trash2,
  CheckCircle,
  Loader2,
  X,
  AlertTriangle,
} from 'lucide-react';
import { adminAPI, lookupAPI, resolveServerUrl } from '../../services/api';

// Types
interface Faculty {
  id: number;
  name: string;
  email: string;
  role: string;
  department_id: number | null;
  department_name: string | null;
  position: string | null;
  bio: string | null;
  is_active: boolean | number;
  joined_date: string;
  total_submissions: number;
  approved_submissions: number;
  profile_picture?: string | null;
  profile_image?: string | null;
}

interface Department {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export default function FacultyManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [faculty, setFaculty] = useState<Faculty[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [facultyToDelete, setFacultyToDelete] = useState<Faculty | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [profilePictures, setProfilePictures] = useState<Record<number, string | null>>({});
  const [viewPicture, setViewPicture] = useState<{ name: string; url: string } | null>(null);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Fetch faculty data function (reusable)
  const fetchFacultyData = async () => {
    try {
      const facultyResponse = await adminAPI.getAllFaculty();
      console.log('Faculty response:', facultyResponse);

      // Handle the stored procedure response format: data = [[users], [{total_count}]]
      if (facultyResponse.data && Array.isArray(facultyResponse.data)) {
        const data = facultyResponse.data;
        if (Array.isArray(data) && data.length >= 1 && Array.isArray(data[0])) {
          const allUsers = data[0] || [];
          const facultyUsers = allUsers.filter((u: any) => u.role === 'faculty');
          setFaculty(facultyUsers);
        } else if (Array.isArray(data)) {
          const facultyUsers = data.filter((u: any) => u.role === 'faculty');
          setFaculty(facultyUsers);
        }
      } else if (facultyResponse.faculty) {
        setFaculty(facultyResponse.faculty);
      } else if (facultyResponse.users) {
        setFaculty(facultyResponse.users);
      } else if (Array.isArray(facultyResponse)) {
        const facultyUsers = facultyResponse.filter((u: any) => u.role === 'faculty');
        setFaculty(facultyUsers);
      }
    } catch (err: any) {
      console.error('Error fetching faculty:', err);
    }
  };

  // Fetch data from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch departments
        const deptResponse = await lookupAPI.getDepartments();
        console.log('Departments response:', deptResponse);

        // Handle different response formats for departments
        if (deptResponse.departments) {
          setDepartments(deptResponse.departments);
        } else if (deptResponse.data && Array.isArray(deptResponse.data)) {
          if (Array.isArray(deptResponse.data[0])) {
            setDepartments(deptResponse.data[0]);
          } else {
            setDepartments(deptResponse.data);
          }
        } else if (Array.isArray(deptResponse)) {
          setDepartments(deptResponse);
        }

        // Fetch faculty
        await fetchFacultyData();
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch profile pictures for each faculty member
  useEffect(() => {
    const fetchProfilePics = async () => {
      for (const f of faculty) {
        if (profilePictures[f.id] !== undefined) continue;
        // First check if the list data already has it
        const existingPic = f.profile_picture || f.profile_image;
        if (existingPic) {
          setProfilePictures((prev) => ({ ...prev, [f.id]: resolveServerUrl(existingPic) }));
          continue;
        }
        try {
          const response = await adminAPI.getFacultyById(f.id);
          let detail: any = null;
          if (response.faculty) detail = response.faculty;
          else if (response.user) detail = response.user;
          else if (response.data) {
            const data = response.data;
            if (Array.isArray(data) && data.length >= 1) {
              if (Array.isArray(data[0]) && data[0].length >= 1) detail = data[0][0];
              else if (typeof data[0] === 'object' && !Array.isArray(data[0])) detail = data[0];
              else detail = data;
            } else detail = data;
          } else if (response.id) detail = response;

          const pic = detail?.profile_picture || detail?.profile_image || null;
          setProfilePictures((prev) => ({ ...prev, [f.id]: resolveServerUrl(pic) }));
        } catch {
          setProfilePictures((prev) => ({ ...prev, [f.id]: null }));
        }
      }
    };
    if (faculty.length > 0) fetchProfilePics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faculty]);

  // Handle delete faculty
  const handleDeleteClick = (faculty: Faculty) => {
    setFacultyToDelete(faculty);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!facultyToDelete) return;

    try {
      setDeleting(true);
      const result = await adminAPI.deleteFaculty(facultyToDelete.id);
      console.log('Delete result:', result);
      
      // Refresh faculty list after deletion
      await fetchFacultyData();
      
      setDeleteDialogOpen(false);
      setFacultyToDelete(null);
    } catch (err: any) {
      console.error('Error deleting faculty:', err);
      alert(err.message || 'Failed to delete faculty');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setFacultyToDelete(null);
  };

  // Filter faculty based on search and department
  const filteredFaculty = faculty.filter((f) => {
    const name = f.name || '';
    const email = f.email || '';
    const matchesSearch =
      name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment =
      departmentFilter === 'all' || f.department_id?.toString() === departmentFilter;
    return matchesSearch && matchesDepartment;
  });

  // Calculate statistics from fetched data
  const activeFacultyCount = faculty.filter((f) => f.is_active === 1 || f.is_active === true).length;
  const totalSubmissions = faculty.reduce((sum, f) => sum + (f.total_submissions || 0), 0);

  return (
    <AdminLayout>
      {/* Picture Viewer Modal */}
      {viewPicture && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/70" onClick={() => setViewPicture(null)} />
          <div className="relative max-w-2xl w-full mx-4">
            <button
              onClick={() => setViewPicture(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={viewPicture.url}
              alt={viewPicture.name}
              className="w-full rounded-xl shadow-2xl"
            />
            <p className="text-center text-white mt-3 font-medium">{viewPicture.name}</p>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && facultyToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={handleDeleteCancel} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <button
              onClick={handleDeleteCancel}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Faculty</h3>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>
            
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <span className="font-semibold">{facultyToDelete.name}</span>? 
              This will deactivate their account and they will no longer be able to access the system.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deleting}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Faculty Management</h1>
            <p className="text-gray-600 mt-1">Manage faculty accounts and view their activity</p>
          </div>
          <div className="mt-4 sm:mt-0">
            <div className="inline-flex items-center px-4 py-2 bg-purple-50 text-purple-700 rounded-lg border border-purple-200">
              <Users className="w-5 h-5 mr-2" />
              <span className="font-semibold">{faculty.length} Faculty Members</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Active Faculty</h3>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : activeFacultyCount}
            </div>
            <p className="text-sm text-gray-600 mt-1">Currently active members</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900">Total Submissions</h3>
            </div>
            <div className="text-3xl font-bold text-gray-900">
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : totalSubmissions}
            </div>
            <p className="text-sm text-gray-600 mt-1">All time submissions</p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building className="h-5 w-5 text-gray-400" />
              </div>
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="block w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id.toString()}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading faculty data...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 rounded-xl border border-red-200 p-6 text-center">
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Faculty Table */}
        {!loading && !error && filteredFaculty.length > 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Desktop View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Faculty Member
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Department
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Submissions
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Approved
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Rejected
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredFaculty.map((f) => (
                    <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div
                            className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200 hover:border-purple-400 transition-colors cursor-pointer"
                            onClick={() => profilePictures[f.id] && setViewPicture({ name: f.name, url: resolveServerUrl(profilePictures[f.id]) })}
                            title={profilePictures[f.id] ? 'Click to view picture' : undefined}
                          >
                            {profilePictures[f.id] ? (
                              <img
                                src={resolveServerUrl(profilePictures[f.id])}
                                alt={f.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-purple-100 flex items-center justify-center text-purple-700 text-sm font-semibold">
                                {getInitials(f.name)}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{f.name}</div>
                            <div className="text-sm text-gray-600">{f.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-700">{f.department_name || 'Not assigned'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{f.total_submissions || 0}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-green-600">{f.approved_submissions || 0}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-red-600">{Math.max(0, (f.total_submissions || 0) - (f.approved_submissions || 0))}</div>
                      </td>
                      <td className="px-6 py-4">
                        <button 
                          onClick={() => handleDeleteClick(f)}
                          className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Delete faculty"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className="lg:hidden divide-y divide-gray-200">
              {filteredFaculty.map((f) => (
                <div key={f.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 flex-1">
                      <div
                        className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200 hover:border-purple-400 transition-colors cursor-pointer"
                        onClick={() => profilePictures[f.id] && setViewPicture({ name: f.name, url: resolveServerUrl(profilePictures[f.id]) })}
                        title={profilePictures[f.id] ? 'Click to view picture' : undefined}
                      >
                        {profilePictures[f.id] ? (
                          <img
                            src={resolveServerUrl(profilePictures[f.id])}
                            alt={f.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-purple-100 flex items-center justify-center text-purple-700 text-sm font-semibold">
                            {getInitials(f.name)}
                          </div>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{f.name}</h3>
                        <p className="text-sm text-gray-600">{f.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Department:</span>
                      <span className="font-medium text-gray-900">{f.department_name || 'Not assigned'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Submissions:</span>
                      <span className="font-medium text-gray-900">{f.total_submissions || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Approved:</span>
                      <span className="font-medium text-green-600">{f.approved_submissions || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Rejected:</span>
                      <span className="font-medium text-red-600">{Math.max(0, (f.total_submissions || 0) - (f.approved_submissions || 0))}</span>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleDeleteClick(f)}
                      className="w-full flex items-center justify-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Faculty
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !loading && !error && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">No faculty members found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
